import { useState } from "react";
import { NavLink, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import {
  LayoutDashboard, Warehouse, Users, LogOut, Container, ClipboardList,
  Factory, Settings, ChevronDown, Truck, ClipboardCheck, PackageCheck,
} from "lucide-react";

// Grouped, department-based navigation. Groups/items only render when the user
// holds the required permission (hide-not-disable). Driver / Gate roles have no
// group permissions, so they see only their role dashboard.
const NAV = [
  { type: "item", name: "Dashboard", icon: LayoutDashboard, path: "/", perm: "dashboard:view" },
  { type: "item", name: "Finished Goods", icon: PackageCheck, path: "/finished-goods", perm: "dashboard:view" },
  {
    type: "group", key: "procurement", name: "Procurement", icon: ClipboardList, perm: "procurement:view", items: [
      { name: "Purchasing", path: "/procurement", perm: "procurement:view" },
      { name: "Suppliers", path: "/suppliers", perm: "procurement:view" },
      { name: "Goods Receiving", path: "/goods-receipt", perm: "procurement:view" },
    ],
  },
  {
    type: "group", key: "warehouse", name: "Warehouse", icon: Warehouse, perm: "warehouse:view", items: [
      { name: "Inventory", path: "/inventory", perm: "inventory:view" },
      { name: "Warehouse Explorer", path: "/warehouses", perm: "warehouse:view" },
    ],
  },
  {
    type: "group", key: "production", name: "Production", icon: Factory, perm: "production:view", items: [
      { name: "Material Requests", path: "/production", perm: "production:view" },
    ],
  },
  { type: "item", name: "Production Requests", icon: ClipboardCheck, path: "/production-requests", permsAny: ["warehouse:view", "production:view"] },
  { type: "item", name: "Logistics", icon: Truck, path: "/logistics", perm: "logistics:view" },
  {
    type: "group", key: "admin", name: "Administration", icon: Settings, permsAny: ["users:view", "finance:view"], items: [
      { name: "Users & Roles", path: "/admin/users", perm: "users:view" },
      { name: "Billing & Plan", path: "/billing", perm: "users:view" },
      { name: "Master Data", path: "/master-data", perm: "masterdata:view" },
      { name: "Finance", path: "/finance", perm: "finance:view" },
      { name: "System Health", path: "/system", perm: "users:view" },
    ],
  },
];

function itemTestId(name) {
  return `nav-${name.toLowerCase().replace(/[^a-z]+/g, "-").replace(/^-|-$/g, "")}`;
}

const linkClass = ({ isActive }) =>
  `flex items-center gap-3 px-3 py-2 rounded-md text-sm font-medium transition-colors ${
    isActive
      ? "bg-orange-600/15 text-orange-400 border border-orange-600/30"
      : "text-slate-400 hover:text-slate-100 hover:bg-slate-800/70 border border-transparent"
  }`;

export default function AppLayout({ children }) {
  const { user, company, logout, hasPerm } = useAuth();
  const navigate = useNavigate();
  const [collapsed, setCollapsed] = useState({});

  const canSee = (perm) => !perm || hasPerm(perm);
  const nodeVisible = (n) => (n.permsAny ? n.permsAny.some((p) => hasPerm(p)) : canSee(n.perm));
  const toggle = (k) => setCollapsed((c) => ({ ...c, [k]: !c[k] }));
  const handleLogout = () => { logout(); navigate("/login"); };

  return (
    <div className="min-h-screen flex bg-slate-950 text-slate-100">
      <aside className="w-64 flex-shrink-0 border-r border-slate-800 bg-slate-900 flex flex-col" data-testid="app-sidebar">
        <div className="h-16 flex items-center gap-3 px-5 border-b border-slate-800">
          <div className="h-9 w-9 rounded-md bg-orange-600 flex items-center justify-center shadow-lg shadow-orange-900/40">
            <Container className="h-5 w-5 text-white" />
          </div>
          <div className="leading-tight">
            <div className="font-heading font-extrabold tracking-wide text-lg uppercase">Onlogist<span className="text-orange-500"> Pro</span></div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500">Manufacturing OS</div>
          </div>
        </div>

        <nav className="flex-1 p-3 space-y-1 overflow-y-auto">
          {NAV.map((entry) => {
            if (entry.type === "item") {
              if (!nodeVisible(entry)) return null;
              return (
                <NavLink key={entry.path} to={entry.path} end={entry.path === "/"}
                  data-testid={itemTestId(entry.name)} className={linkClass}>
                  <entry.icon className="h-4 w-4" /> {entry.name}
                </NavLink>
              );
            }
            const visible = entry.items.filter((i) => nodeVisible(i));
            if (!nodeVisible(entry)) return null;
            if (visible.length === 0) return null;
            const isOpen = !collapsed[entry.key];
            return (
              <div key={entry.key} className="pt-2">
                <button
                  onClick={() => toggle(entry.key)}
                  data-testid={`nav-group-${entry.key}`}
                  className="w-full flex items-center justify-between px-3 py-1.5 text-[10px] font-mono uppercase tracking-wider text-slate-500 hover:text-slate-300 transition-colors"
                >
                  <span className="flex items-center gap-2"><entry.icon className="h-3.5 w-3.5" /> {entry.name}</span>
                  <ChevronDown className={`h-3.5 w-3.5 transition-transform ${isOpen ? "" : "-rotate-90"}`} />
                </button>
                {isOpen && (
                  <div className="mt-1 space-y-1">
                    {visible.map((item) => (
                      <NavLink key={item.path} to={item.path} data-testid={itemTestId(item.name)} className={linkClass}>
                        <span className="h-1.5 w-1.5 rounded-full bg-slate-600 ml-1.5" /> {item.name}
                      </NavLink>
                    ))}
                  </div>
                )}
              </div>
            );
          })}
        </nav>

        <div className="p-3 border-t border-slate-800">
          <div className="px-3 py-2 mb-2">
            {company?.name && <div className="font-mono text-[10px] uppercase tracking-wider text-slate-500 truncate" data-testid="sidebar-company">{company.name}</div>}
            <div className="text-sm font-semibold truncate">{user?.name}</div>
            <div className="font-mono text-[10px] uppercase tracking-wider text-orange-500">{(user?.role || "").replace(/_/g, " ")}</div>
          </div>
          <button onClick={handleLogout} data-testid="logout-button"
            className="w-full flex items-center gap-2 px-3 py-2 rounded-md text-sm text-slate-400 hover:text-rose-400 hover:bg-rose-500/10 transition-colors">
            <LogOut className="h-4 w-4" /> Sign out
          </button>
        </div>
      </aside>

      <div className="flex-1 flex flex-col min-w-0">
        <header className="h-16 flex items-center justify-between px-8 border-b border-slate-800 bg-slate-900/50 backdrop-blur">
          <div className="font-mono text-xs uppercase tracking-wider text-slate-500">
            ONLOGIST PRO · Operational Command Center
          </div>
          <div className="flex items-center gap-2 font-mono text-[11px] text-emerald-400">
            <span className="h-2 w-2 rounded-full bg-emerald-400 animate-pulse" /> LIVE
          </div>
        </header>
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-auto">{children}</main>
      </div>
    </div>
  );
}
